import { useTranslation } from "react-i18next";
import { GripVertical, X } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import type { TWidgetSizeKey } from "../types";
import { SizeMenu } from "./SizeMenu";

interface WidgetChromeProps {
  widgetName: string;
  sizes: TWidgetSizeKey[];
  size: TWidgetSizeKey;
  /** dnd-kit's activator wiring — makes the grip the keyboard entry point for reordering. */
  activatorRef: (element: HTMLElement | null) => void;
  activatorProps: Record<string, unknown>;
  onResize: (size: TWidgetSizeKey) => void;
  onRemove: () => void;
}

/**
 * The customize-mode control cluster.
 *
 * Straddles the card's top edge instead of sitting inside it: an earlier version
 * put a control strip over the widget's own header, which meant you lost sight of
 * the thing you were arranging exactly while arranging it. Half-outside costs a
 * few pixels of the header row and keeps every number on screen.
 *
 * Always visible in customize mode rather than on hover — a control you have to
 * discover by hovering is a control most people never find, and hover does not
 * exist on touch at all.
 *
 * Three controls, always the same three, at every card size. That is what the
 * size menu bought: the cluster used to hold an inline strip of up to six size
 * segments, which made the pill wider than the `xs` card it floated over, so it
 * was capped to the card's width and given a horizontal scroll to absorb the
 * difference — a control you swipe through, on the smallest widget, in the mode
 * where precision matters most. With a single trigger in its place the cluster
 * is a fixed ~100px and the cap, the scroll, and the `min-w-0` reasoning they
 * needed all go away.
 *
 * Each control is a 20px circle inside a 24px hit area (`.widget-target`) rather
 * than a 20px target — see the control ergonomics note in global.css. The ink
 * stays 20px because the pill floats over a card's own content and a visibly
 * larger cluster hides more of it.
 *
 * Both tooltips open downward (`side="bottom"`), onto the card rather than off
 * into the gap above it. The chrome already floats at `-top-3`, so a
 * default-`top` tooltip has almost no room before it reaches whatever sits
 * above the canvas — the command bar, for the first row of widgets — and
 * Radix's collision handling only pushes a tooltip back from the *viewport*
 * edge, not away from an unrelated sibling like that bar. Below the trigger
 * there is a whole card to spare.
 */
export const WidgetChrome = ({
  widgetName,
  sizes,
  size,
  activatorRef,
  activatorProps,
  onResize,
  onRemove,
}: WidgetChromeProps) => {
  const { t } = useTranslation(["common"]);

  return (
    <div
      // The card itself is the drag surface; the cluster must not inherit that or
      // every click on a control would start a drag.
      onPointerDown={(event) => event.stopPropagation()}
      className={cn(
        "widget-chrome absolute -top-3 end-2 z-30 flex items-center gap-1",
        // Every control here is 20px tall inside a 24px target, so each overhangs
        // its own ground by 2px vertically; `py-1.5` is what keeps that overhang
        // on the frosted pill rather than out over the card. `gap-1` does the
        // same job horizontally for the two 20px-wide controls — 4px is exactly
        // two overhangs, so neighbouring targets meet without overlapping, and an
        // overlap would hand the shared strip to whichever control happens to
        // come later in the DOM. Cluster width is fixed at ~88px, which clears
        // even a 2-column `xs` card.
        "rounded-full px-1.5 py-1.5",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            ref={activatorRef}
            // Focus target after a removal — see DashboardGrid's focus recovery.
            data-widget-grip
            aria-label={`Reorder ${widgetName}. Press space, then use the arrow keys.`}
            className="widget-target widget-focus grid size-5 shrink-0 cursor-grab place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            {...activatorProps}
          >
            <GripVertical size={12} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {t("common:dashboard_grid.drag_to_reorder")}
        </TooltipContent>
      </Tooltip>

      {sizes.length > 1 && (
        <SizeMenu
          widgetName={widgetName}
          sizes={sizes}
          value={size}
          onChange={onResize}
        />
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Remove ${widgetName} from the dashboard`}
            onClick={onRemove}
            className="widget-target widget-focus grid size-5 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-danger hover:text-danger-foreground"
          >
            <X size={12} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {t("common:dashboard_grid.remove")}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
